'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { LayoutGroup, motion } from 'framer-motion';
import React, { Fragment, forwardRef, useId } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';

/* ---------- Conteneurs (Sidebar, SidebarHeader, …) ---------------------------------------------------- */

export function Sidebar({ className, ...props }) {
  return <nav {...props} className={clsx(className, 'flex h-full min-h-0 flex-col')} />;
}

export function SidebarHeader({ className, ...props }) {
  return (
      <div
          {...props}
          className={clsx(
              className,
              'flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
          )}
      />
  );
}

export function SidebarBody({ className, ...props }) {
  return (
      <div
          {...props}
          className={clsx(
              className,
              'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8'
          )}
      />
  );
}

export function SidebarFooter({ className, ...props }) {
  return (
      <div
          {...props}
          className={clsx(
              className,
              'flex flex-col border-t border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
          )}
      />
  );
}

export function SidebarSection({ className, ...props }) {
  const id = useId();
  return (
      <LayoutGroup id={id}>
        <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-0.5')} />
      </LayoutGroup>
  );
}

export function SidebarDivider({ className, ...props }) {
  return <hr {...props} className={clsx(className, 'my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5')} />;
}

export function SidebarSpacer({ className, ...props }) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />;
}

export function SidebarHeading({ className, ...props }) {
  return (
      <h3 {...props} className={clsx(className, 'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400')} />
  );
}

/* ---------- Item ---------------------------------------------------- */

// forwardRef pour exposer le ref vers le <span> exterior ou le <Link>/<Button> interne
export const SidebarItem = forwardRef(function SidebarItem(
    {
      current,        // indique si c’est l’item actif
      className,      // classes supplémentaires
      children,       // contenu (icône + libellé)
      onClick,        // callback au clic
      onClickClose,   // callback optionnel pour fermer un menu mobile
      ...props        // reste des props (notamment href, target…)
    },
    ref
) {
  // construction des classes communes
  const classes = clsx(
      'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-zinc-950 sm:py-2 sm:text-sm/5',
      'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-zinc-500 sm:data-[slot=icon]:*:size-5',
      'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4',
      'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
      'data-[hover]:bg-red-950/5 data-[slot=icon]:*:data-[hover]:fill-blue-950',
      'data-[active]:bg-green-950/5 data-[slot=icon]:*:data-[active]:fill-green-950',
      'data-[slot=icon]:*:data-[current]:fill-green-950',
      'dark:text-white dark:data-[slot=icon]:*:fill-zinc-400',
      'dark:data-[hover]:bg-white/5 dark:data-[slot=icon]:*:data-[hover]:fill-white',
      'dark:data-[active]:bg-white/5 dark:data-[slot=icon]:*:data-[active]:fill-white',
      'dark:data-[slot=icon]:*:data-[current]:fill-white'
  );

  // wrapper principal pour positionner l’indicateur courant
  return (
      <span ref={ref} className={clsx(className, 'relative')} {...(props.href ? {} : {})}>
      {/* indicateur visuel animé si actif */}
        {current && (
            <motion.span
                layoutId="current-indicator"
                className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-zinc-950 dark:bg-white"
            />
        )}

        {/*
        Si props contient href, on rend un <Link>, sinon un <Button>.
        Dans les deux cas, on appelle onClick puis onClickClose si fourni.
      */}
        {'href' in props ? (
            <Link
                {...props}
                className={classes}
                data-current={current ? 'true' : undefined}
                onClick={(e) => {
                  onClick?.(e);
                  onClickClose?.();
                }}
            >
              <TouchTarget>{children}</TouchTarget>
            </Link>
        ) : (
            <Headless.Button
                {...props}
                className={clsx('cursor-default', classes)}
                data-current={current ? 'true' : undefined}
                onClick={(e) => {
                  onClick?.(e);
                  onClickClose?.();
                }}
            >
              <TouchTarget>{children}</TouchTarget>
            </Headless.Button>
        )}
    </span>
  );
});

/* ---------- Label ---------------------------------------------------- */

export function SidebarLabel({ className, ...props }) {
  // simple <span> tronqué si trop long
  return <span {...props} className={clsx(className, 'truncate')} />;
}
